// components
import TextField from '@mui/material/TextField';

// hooks
import { useEffect, useState } from 'react';
import { useBufferedCommit } from 'hooks/useBufferedCommit';

type ReasonFieldProps = {
  value: string;
  onCommit: (reason: string) => void;
  label: string;
  /** shown once the field has been visited and left empty */
  requiredMessage: string;
  /** shows the required message without waiting for a blur, for when the
   *  missing reason is the only thing still blocking Save */
  highlightRequired?: boolean;
};

/**
 * The reason for the sickness notice. This field re-renders on every keystroke,
 * the dialog only once the planner pauses — see `useBufferedCommit` for why.
 * Blur and unmount flush, so the reason a planner typed can never be lost by
 * clicking Save straight away.
 */
const ReasonField = ({
  value,
  onCommit,
  label,
  requiredMessage,
  highlightRequired = false
}: ReasonFieldProps) => {
  const [text, setText] = useState(value);
  const [touched, setTouched] = useState(false);
  const buffer = useBufferedCommit(onCommit);

  // an external change (a notice prefilling the reason, an absence loading)
  // refreshes the buffer; our own commits leave the typing alone
  useEffect(() => {
    setText((current) =>
      buffer.hasPending() || current === value ? current : value
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={text}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        if (raw === value) {
          buffer.cancel();
          return;
        }

        // Whether there IS a reason decides whether the save button works, so
        // the first character — and emptying the field again — has to arrive at
        // once. Waiting for the pause there left the button grey while the
        // planner was already reaching for it.
        if (!!raw.trim() !== !!value.trim()) {
          buffer.commitNow(raw);
          return;
        }
        buffer.schedule(raw);
      }}
      // clicking Save right after typing has to save that reason: the blur that
      // the click starts with commits it before the button is pressed
      onBlur={() => {
        buffer.flush();
        setTouched(true);
      }}
      error={(touched || highlightRequired) && !text.trim()}
      helperText={
        (touched || highlightRequired) && !text.trim()
          ? requiredMessage
          : undefined
      }
      multiline
      minRows={2}
    />
  );
};

export default ReasonField;
